import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, INTERACTION_TYPES, SOURCES, EVENT_TYPE_MAP, JUDETE, formatApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/date";
import { ArrowLeft, Phone, MessageCircle, MapPin, Car as CarIcon, FileText, Trash2, CalendarDays, Plus } from "lucide-react";
import { toast } from "sonner";
import EventDialog from "@/components/EventDialog";

const ICONS = { call: Phone, message: MessageCircle, visit: MapPin, test_drive: CarIcon, offer: FileText };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [cars, setCars] = useState([]);
  const [newInt, setNewInt] = useState({ type: "call", note: "", car_id: "" });
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  async function load() {
    try {
      const r = await api.get(`/clients/${id}`);
      setClient(r.data);
      const c = await api.get(`/cars`);
      setCars(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function save(patch) {
    try {
      const body = {
        name: client.name, phone: client.phone, email: client.email,
        source: client.source,
        county: client.county || "",
        city: client.city || "",
        interest_car_id: client.interest_car_id || null,
        notes: client.notes, ...patch,
      };
      const { data } = await api.put(`/clients/${id}`, body);
      setClient({ ...data, interactions: client.interactions, events: client.events });
      toast.success("Salvat");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function addInteraction(e) {
    e.preventDefault();
    try {
      await api.post(`/interactions`, { client_id: id, ...newInt, car_id: newInt.car_id || null });
      setNewInt({ type: "call", note: "", car_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function deleteInteraction(iid) { await api.delete(`/interactions/${iid}`); load(); }

  async function deleteClient() {
    if (!window.confirm("Ștergi clientul și interacțiunile asociate?")) return;
    await api.delete(`/clients/${id}`);
    navigate("/clients");
  }

  if (!client) return <div className="sm:pl-52 text-slate-400">Se încarcă...</div>;

  return (
    <div className="sm:pl-52 space-y-5" data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Înapoi la clienți
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Client</div>
            <h1 className="font-display text-3xl tracking-tight font-medium">{client.name}</h1>
            <div className="text-sm text-slate-500 mt-1">{client.phone || "—"} {client.email ? "· " + client.email : ""}</div>
            {(client.county || client.city) && (
              <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {[client.city, client.county].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
          <button onClick={deleteClient} className="text-slate-400 hover:text-red-600" data-testid="delete-client-btn"><Trash2 className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          {[
            ["Nume", "name"], ["Telefon", "phone"], ["Email", "email"],
          ].map(([label, key]) => (
            <div key={key}>
              <label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</label>
              <input
                data-testid={`client-edit-${key}`}
                value={client[key] ?? ""}
                onChange={(e) => setClient({ ...client, [key]: e.target.value })}
                className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
              />
            </div>
          ))}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Sursă</label>
            <select data-testid="client-edit-source" value={client.source || ""} onChange={(e) => setClient({ ...client, source: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">—</option>
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Județ</label>
            <select data-testid="client-edit-county" value={client.county || ""} onChange={(e) => setClient({ ...client, county: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">—</option>
              {JUDETE.map((j) => <option key={j}>{j}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Localitate</label>
            <input
              data-testid="client-edit-city"
              value={client.city ?? ""}
              onChange={(e) => setClient({ ...client, city: e.target.value })}
              placeholder="Ex: Oradea, Cluj-Napoca…"
              className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Mașină de interes</label>
            <select data-testid="client-edit-interest" value={client.interest_car_id || ""} onChange={(e) => setClient({ ...client, interest_car_id: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">—</option>
              {cars.filter((c) => c.status !== "vanduta" || c.id === client.interest_car_id).map((c) => <option key={c.id} value={c.id}>{c.make} {c.model} · {c.vin?.slice(-6) || c.year || "—"}{c.status === "vanduta" ? " (vândută)" : ""}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Notițe</label>
            <textarea data-testid="client-edit-notes" rows={2} value={client.notes || ""} onChange={(e) => setClient({ ...client, notes: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <div className="col-span-2 flex justify-end">
            <button onClick={() => save({})} className="bg-[#0047FF] text-white px-4 py-2 rounded-lg text-sm" data-testid="client-save-btn">Salvează</button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Istoric</div>
        <h2 className="font-display text-lg font-medium mb-3">Interacțiuni</h2>

        <form onSubmit={addInteraction} className="grid grid-cols-12 gap-2 mb-4">
          <select data-testid="int-type" value={newInt.type} onChange={(e) => setNewInt({ ...newInt, type: e.target.value })} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
            {INTERACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select data-testid="int-car" value={newInt.car_id} onChange={(e) => setNewInt({ ...newInt, car_id: e.target.value })} className="col-span-4 border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
            <option value="">— mașină —</option>
            {cars.map((c) => <option key={c.id} value={c.id}>{c.make} {c.model}</option>)}
          </select>
          <input data-testid="int-note" placeholder="Notă" value={newInt.note} onChange={(e) => setNewInt({ ...newInt, note: e.target.value })} className="col-span-3 border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
          <button data-testid="int-add" className="col-span-2 bg-[#0A0F1C] text-white rounded-lg text-sm">Adaugă</button>
        </form>

        <div className="relative pl-5 space-y-3 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
          {client.interactions.map((i) => {
            const Icon = ICONS[i.type] || Phone;
            return (
              <div key={i.id} className="relative" data-testid={`timeline-${i.id}`}>
                <div className="absolute -left-5 top-1 w-3 h-3 rounded-full bg-[#0047FF] ring-4 ring-white" />
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <div>
                      <div className="text-sm font-medium">{INTERACTION_TYPES.find((t) => t.value === i.type)?.label || i.type}</div>
                      <div className="text-xs text-slate-500">{formatDateTime(i.date)}</div>
                      {i.note && <div className="text-sm mt-1">{i.note}</div>}
                    </div>
                  </div>
                  <button onClick={() => deleteInteraction(i.id)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
          {client.interactions.length === 0 && <div className="text-sm text-slate-400">Nicio interacțiune încă.</div>}
        </div>
      </div>

      {/* Calendar events for this client */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-medium flex items-center gap-2"><CalendarDays className="w-4 h-4" />Activități programate</h2>
          <button
            data-testid="client-add-event"
            onClick={() => { setEditingEvent(null); setEventDialogOpen(true); }}
            className="bg-[#0047FF] hover:bg-[#003BD3] text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Eveniment
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {(client.events || []).map((ev) => {
            const t = EVENT_TYPE_MAP[ev.event_type] || EVENT_TYPE_MAP.custom;
            const dt = new Date(ev.start_datetime);
            const past = dt < new Date() && ev.status !== "done";
            return (
              <button
                key={ev.id}
                onClick={() => { setEditingEvent(ev); setEventDialogOpen(true); }}
                className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-slate-50 -mx-2 px-2 rounded transition-colors"
                data-testid={`client-event-${ev.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0" style={{ backgroundColor: t.color + "22", color: t.color }}>
                    {t.emoji}
                  </span>
                  <div className="min-w-0">
                    <div className={`font-medium text-sm truncate ${ev.status === "done" ? "line-through text-slate-400" : ""}`}>{ev.title}</div>
                    <div className={`text-xs ${past ? "text-rose-600" : "text-slate-500"}`}>
                      {formatDate(dt)} · {ev.all_day ? "toată ziua" : dt.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })} · {t.label}
                    </div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${
                  ev.status === "done" ? "bg-emerald-50 text-emerald-700"
                  : ev.status === "cancelled" ? "bg-slate-100 text-slate-500"
                  : past ? "bg-red-50 text-red-600"
                  : "bg-blue-50 text-blue-700"
                }`}>
                  {ev.status === "done" ? "Finalizat" : ev.status === "cancelled" ? "Anulat" : past ? "Întârziat" : "Programat"}
                </span>
              </button>
            );
          })}
          {(client.events || []).length === 0 && <div className="text-sm text-slate-400">Niciun eveniment programat. Apasă „+ Eveniment" pentru a adăuga.</div>}
        </div>
      </div>

      <EventDialog
        open={eventDialogOpen}
        event={editingEvent}
        defaultClientId={id}
        onClose={(refresh) => {
          setEventDialogOpen(false);
          setEditingEvent(null);
          if (refresh) load();
        }}
      />
    </div>
  );
}
